import { useRouteContext } from '@tanstack/react-router';
import { buildAlternates, resolveSlugSeo, Seo } from '@/routes/-components/seo';
import { wwwLocales } from './locales';

/**
 * Fixes what must not drift between pages: the locale set, the site name and the canonical's shape.
 * `slugs` is the object the router's rewrite reads, so a canonical cannot claim a path it won't serve.
 */
export function PageSeo({
	slugs,
	title,
	description,
	noindex,
	jsonLd,
}: {
	/** Empty for the homepage, which lives at the locale root. */
	slugs: Record<string, string>;
	title: string;
	description: string;
	noindex?: boolean;
	/** Build on-page content and its markup from one array so they cannot drift. */
	jsonLd?: Record<string, unknown>[];
}) {
	const { baseUrl, locale, hasLocalePrefix } = useRouteContext({ from: '__root__' });

	// `resolveSlugSeo` would write `/es/undefined` for the homepage's empty map.
	const isHome = Object.keys(slugs).length === 0;
	const { canonicalUrl, alternates } = isHome
		? {
				canonicalUrl: hasLocalePrefix ? `${baseUrl}/${locale}` : baseUrl,
				alternates: buildAlternates(baseUrl, '', wwwLocales),
			}
		: resolveSlugSeo(hasLocalePrefix, baseUrl, locale, wwwLocales, slugs);

	return (
		<Seo
			title={title}
			description={description}
			siteName="Worldcoo"
			canonicalUrl={canonicalUrl}
			locale={locale}
			alternateLocales={[...wwwLocales]}
			alternates={alternates}
			noindex={noindex}
			jsonLd={jsonLd}
		/>
	);
}
